import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { useJoinTournament } from '../hooks/useJoinTournament';
import GasBanner from './GasBanner';
import './TournamentLobby.css';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const REFRESH_INTERVAL_FILLING = 5_000;   // fast poll while lobby is filling
const REFRESH_INTERVAL_ACTIVE  = 30_000;  // slower once match window is running
const MAX_PLAYERS = 100;

function formatUSDC(raw) {
  if (!raw) return '$0';
  return `$${(Number(raw) / 1_000_000).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function useCountdown(windowEnd) {
  const [label, setLabel] = useState('');
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (!windowEnd) return;
    function tick() {
      const diff = windowEnd * 1000 - Date.now();
      if (diff <= 0) { setLabel('Window closed'); setExpired(true); return; }
      const h = Math.floor(diff / 3_600_000);
      const m = Math.floor((diff % 3_600_000) / 60_000);
      const s = Math.floor((diff % 60_000) / 1_000);
      setLabel(`${h}h ${String(m).padStart(2,'0')}m ${String(s).padStart(2,'0')}s`);
      setExpired(false);
    }
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [windowEnd]);

  return { label, expired };
}

function TournamentCard({ tournament, walletAddress }) {
  const [mode, setMode] = useState('idle'); // idle | form | tx | success
  const [apexId, setApexId] = useState('');
  const [formError, setFormError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const { label: timerLabel, expired } = useCountdown(tournament.window_end);
  const { joinTournament, state: joinState, reset: resetJoin } = useJoinTournament();

  const pct = Math.min((tournament.player_count / MAX_PLAYERS) * 100, 100);

  const stepLabel = {
    approving:      'Step 1/3: Approving USDC...',
    joining:        'Step 2/3: Joining on-chain...',
    fetching_stats: 'Step 3/3: Fetching your current stats...',
    recording:      'Step 3/3: Fetching your current stats...',
  }[joinState.step];

  async function handleJoin(e) {
    e.preventDefault();
    if (!apexId.trim()) { setFormError('EA / Origin Username is required'); return; }
    setFormError('');
    setMode('tx');
    try {
      const data = await joinTournament({
        tournament,
        gamePlayerId: apexId.trim(),
        gameField:    'apexPlayerId',
      });
      setSuccessData(data);
      setMode('success');
    } catch (err) {
      setFormError(err?.response?.data?.error || err?.message || 'Failed to join. Try again.');
      setMode('form');
    }
  }

  return (
    <div className="t-card">
      <div>
        <div className="t-card__prize-label">Winner Takes (90%)</div>
        <div className="t-card__prize">{formatUSDC(Math.floor(Number(tournament.prize_pool) * 0.9))}</div>
        <div className="t-card__prize-pool-sub">Total pool: {formatUSDC(tournament.prize_pool)} · 10% platform fee</div>
      </div>

      <div className="t-card__meta">
        <span className="t-card__entry">Entry: {formatUSDC(tournament.entry_fee)}</span>
        {tournament.status === 'active' && (
          <span className={`t-card__timer${expired ? ' t-card__timer--expired' : ''}`}>
            {timerLabel}
          </span>
        )}
        {tournament.status === 'filling' && (
          <span className="t-card__badge-filling">● Filling</span>
        )}
      </div>

      {tournament.status === 'active' && (
        <div style={{ fontSize: 11, color: '#00D4FF', letterSpacing: '1px', textTransform: 'uppercase', fontWeight: 600 }}>
          30 min match window · 1 match · instant payout
        </div>
      )}

      {tournament.status === 'filling' && (
        <div className="t-card__filling-banner">
          <div className="t-card__filling-title">⏳ Waiting for full lobby</div>
          <div className="t-card__filling-sub">
            The 30-minute match window starts the moment slot 100 fills.
            Join now and stay ready — it could be seconds away.
          </div>
        </div>
      )}

      <div>
        <div className="t-card__progress-row">
          <span>Players</span>
          <span>{tournament.player_count} / {MAX_PLAYERS}</span>
        </div>
        <div className="t-card__bar-track">
          <div className="t-card__bar-fill" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {tournament.players && tournament.players.length > 0 && (
        <div className="t-card__players">
          <div className="t-card__players-label">Enrolled players</div>
          <div className="t-card__players-list">
            {tournament.players.map((p, i) => (
              <div key={i} className="t-card__player-row">
                <span className="t-card__player-handle">{p.handle}</span>
                <span className="t-card__player-wallet">{p.wallet}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {mode === 'idle' && (
        <button
          className="t-card__join-btn"
          onClick={() => setMode('form')}
          disabled={tournament.player_count >= MAX_PLAYERS || (tournament.status === 'active' && expired)}
        >
          {tournament.player_count >= MAX_PLAYERS
            ? 'Full'
            : (tournament.status === 'active' && expired)
              ? 'Closed'
              : 'Join'}
        </button>
      )}

      {(mode === 'form' || mode === 'tx') && (
        <form className="t-card__form" onSubmit={handleJoin}>
          <div className="t-card__gas-notice">
            ⛽ Need POL for gas?{' '}
            <a
              href="https://app.uniswap.org/swap?chain=polygon&inputCurrency=0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359&outputCurrency=MATIC&exactAmount=0.10&exactField=input"
              target="_blank"
              rel="noopener noreferrer"
              className="t-card__gas-link"
            >
              Swap $0.10 USDC → POL →
            </a>
          </div>
          <input
            className="t-card__input"
            placeholder="EA / Origin Username"
            value={apexId}
            onChange={e => setApexId(e.target.value)}
            disabled={mode === 'tx'}
            autoFocus
          />
          {formError && <span className="t-card__form-error">{formError}</span>}
          {mode === 'tx' && stepLabel && (
            <span className="t-card__form-step">{stepLabel}</span>
          )}
          <div className="t-card__form-row">
            <button
              type="button"
              className="t-card__cancel-btn"
              onClick={() => { setMode('idle'); setApexId(''); setFormError(''); resetJoin(); }}
              disabled={mode === 'tx'}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="t-card__submit-btn"
              disabled={mode === 'tx'}
            >
              {mode === 'tx' ? 'Processing...' : 'Confirm'}
            </button>
          </div>
        </form>
      )}

      {mode === 'success' && successData && (
        <div className="t-card__success">
          <div className="t-card__success-title">✅ All good, you're in!</div>
          <div className="t-card__success-detail">
            {successData.tournament.status === 'active' ? (
              <>
                Match window is live — launch Apex and queue ranked now!<br />
                {new Date(successData.tournament.window_start * 1000).toLocaleString()} →{' '}
                {new Date(successData.tournament.window_end * 1000).toLocaleString()}
              </>
            ) : (
              <>
                Waiting for tournament to start ({successData.tournament.player_count}/{successData.tournament.max_players || 100} players).<br />
                <strong style={{ color: '#00D4FF' }}>Stay ready</strong> — the window starts the moment the last spot fills. Launch Apex and queue ranked now!
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function TournamentLobby({ walletAddress }) {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const location = useLocation();

  // Store referral code from URL into localStorage
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get('ref');
    if (ref) localStorage.setItem('referralCode', ref);
  }, [location.search]);

  const fetchTournaments = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/tournament/active?game=apex`);
      setTournaments(data.tournaments);
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load tournaments. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTournaments();
  }, [fetchTournaments]);

  // Poll fast when any lobby is still filling, slower once all are active
  useEffect(() => {
    const hasFilling = tournaments.some(t => t.status === 'filling');
    const interval = hasFilling ? REFRESH_INTERVAL_FILLING : REFRESH_INTERVAL_ACTIVE;
    const id = setInterval(fetchTournaments, interval);
    return () => clearInterval(id);
  }, [tournaments, fetchTournaments]);

  return (
    <div className="lobby">
      <Helmet>
        <title>Apex Legends Tournaments — Win USDC for Most Kills | EZGAME</title>
        <meta name="description" content="Join an Apex Legends kill tournament for $1 USDC. Most kills in a shared ranked match wins 90% of the prize pool, paid instantly on Polygon." />
        <link rel="canonical" href="https://www.ezegames.live/lobby" />
        <meta property="og:title" content="Apex Legends Kill Tournaments — Win USDC | EZGAME" />
        <meta property="og:description" content="$1 entry. One ranked match. 90% prize pool to the top Apex player — paid instantly in USDC on Polygon." />
        <meta property="og:url" content="https://www.ezegames.live/lobby" />
      </Helmet>
      <GasBanner />
      <div className="lobby__header">
        <h1 className="lobby__title">Active <span>Tournaments</span></h1>
        {lastRefresh && (
          <span className="lobby__refresh-badge">
            Updated {lastRefresh.toLocaleTimeString()}
          </span>
        )}
      </div>

      {error && <div className="lobby__error">{error}</div>}

      {loading && <div className="lobby__status">Loading tournaments...</div>}

      {!loading && !error && tournaments.length === 0 && (
        <div className="lobby__status">No active tournaments right now.</div>
      )}

      {!loading && tournaments.length > 0 && (
        <div className="lobby__grid">
          {tournaments.map(t => (
            <TournamentCard key={t.id} tournament={t} walletAddress={walletAddress} />
          ))}
        </div>
      )}
    </div>
  );
}
